import { createContext, PropsWithChildren, useCallback, useEffect, useState } from 'react'
import { AuthProvider as ComponentAuthProvider, AuthContextInterface } from '@surveying-hub-bv/fe-component-library'
import { useFirebaseApp } from './firebase'
import { User } from 'firebase/auth'
import { OrganizationService, TeamInfo } from './services/OrganizationService'
import { TrackingService } from './services/TrackingService'


export type PossibleTeam = TeamInfo[] | 'loading' | undefined

export function isTeamArray(value: PossibleTeam): value is TeamInfo[] {
    return Array.isArray(value)
}

export interface ExtendedAuthContextInterface extends AuthContextInterface {
    teams: TeamInfo[] | 'loading' | undefined
    isAdmin: boolean | 'loading' | undefined
    refetch: () => void
    uid: string
    getToken: () => Promise<string>
}

export const ExtendedAuthContext = createContext<ExtendedAuthContextInterface | 'loading' | undefined>(undefined)
export type NonLoadingExtendedAuthContext = ExtendedAuthContextInterface | undefined
export const nonLoading = (isLoggedIn: ExtendedAuthContextInterface | 'loading' | undefined): NonLoadingExtendedAuthContext => (isLoggedIn === 'loading') ? undefined : isLoggedIn

const AuthProvider = ({ children }: PropsWithChildren) => {
    const { isLoggedIn, logout } = useAuth()
    const [teams, setTeams] = useState<TeamInfo[] | 'loading' | undefined>('loading')
    const [isAdmin, setIsAdmin] = useState<boolean | 'loading' | undefined>('loading')
    const fetchUserInfo = useCallback(async () => {
        if (isSet(isLoggedIn)) {
            try {
                const token = await isLoggedIn.getIdToken()
                const userInfo = await OrganizationService.fetchOrganizations(token)

                setTeams(userInfo.teams)
                setIsAdmin(userInfo.isAdmin)
                TrackingService.reportLogin(isLoggedIn.uid, userInfo.isAdmin ? 'admin' : 'user')
            } catch (error) {
                setTeams(undefined)
                setIsAdmin(undefined)
                console.error('Failed to fetch userInfo status', error)
            }
        }
    }, [isLoggedIn])
    useEffect(() => {
        fetchUserInfo()
    }, [fetchUserInfo])


    const contextValue: ExtendedAuthContextInterface | 'loading' | undefined = (() => {
        if (!isSet(isLoggedIn)) return isLoggedIn
        else return ({
            username: isLoggedIn.displayName || "",
            email: isLoggedIn.email || undefined,
            photoURL: isLoggedIn.photoURL || undefined,
            uid: isLoggedIn.uid,
            teams,
            isAdmin,
            refetch: fetchUserInfo,
            getToken: async () => await isLoggedIn.getIdToken(),
            logout
        })
    })()

    return (
        <ComponentAuthProvider value={contextValue}>
            {children}
        </ComponentAuthProvider>
    )
}

function isSet(isLoggedIn: User | 'loading' | undefined): isLoggedIn is User {
    return (isLoggedIn !== 'loading' && (isLoggedIn !== undefined))
}

export default AuthProvider

export function useAuth() {
    const auth = useFirebaseApp()
    const [isLoggedIn, setIsLoggedIn] = useState<User | 'loading' | undefined>('loading')

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(user => {
            if (user) {
                setIsLoggedIn(user)
            } else {
                setIsLoggedIn(undefined)
            }
        })
        return unsubscribe
    }, [auth])

    const logout = async () => {
        await auth.signOut()
    }
    return { isLoggedIn, logout }
}
